import { ChevronDown, Clapperboard, Menu } from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ConnectionControl } from './connection-control';
import { NAV_ITEMS } from './nav-items';
import { SearchBox } from './search-box';
import { ThemeSelector } from './theme-selector';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { urls } from '@/lib/urls';
import { cn } from '@/lib/utils';

const LINK_CLASSES = 'rounded-button px-3 py-1.5 text-sm text-muted-foreground transition-theme hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const ACTIVE_CLASSES = 'font-semibold text-foreground';

export function TopBar() {
  const location = useLocation();
  const active = (to: string, prefix?: string) => (prefix !== undefined ? location.pathname.startsWith(prefix) : location.pathname === to);
  const libraryItems = NAV_ITEMS.filter((item) => item.library === true);
  const firstLibrary = libraryItems[0];
  const inLibrary = libraryItems.some((item) => active(item.to, item.matchPrefix)) || active(urls.hidden());

  return (
    <header className="sticky top-0 z-30 border-b border-border/60 bg-[var(--nav-bg)] backdrop-blur-md transition-theme">
      {/* The playful theme's theatre lights; empty in the others. */}
      <div className="theatre-lights pointer-events-none absolute inset-x-0 -bottom-[6px] h-[10px] opacity-70" aria-hidden />
      <div className="mx-auto flex h-14 max-w-[100rem] items-center gap-3 px-4 sm:px-6 lg:px-8">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open navigation">
              <Menu className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {NAV_ITEMS.map((item) => (
              <DropdownMenuItem key={item.to} asChild>
                <Link to={item.to}>
                  <item.icon className="size-4" />
                  {item.label}
                </Link>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to={urls.hidden()}>Hidden movies</Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Link to={urls.home()} className="flex shrink-0 items-center gap-2 rounded font-semibold tracking-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <Clapperboard className="size-5 text-primary" />
          <span className="font-display text-xl">zyx</span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="Main">
          {NAV_ITEMS.filter((item) => item.library !== true || item === firstLibrary).map((item) =>
            item === firstLibrary ? (
              <DropdownMenu key="library">
                <DropdownMenuTrigger className={cn(LINK_CLASSES, 'inline-flex items-center gap-1', inLibrary && ACTIVE_CLASSES)}>
                  Library
                  <ChevronDown className="size-3.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {libraryItems.map((entry) => (
                    <DropdownMenuItem key={entry.to} asChild>
                      <Link to={entry.to} className={cn(active(entry.to, entry.matchPrefix) && 'font-medium')}>
                        <entry.icon className="size-4" />
                        {entry.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={urls.hidden()}>Hidden movies</Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <NavLink key={item.to} to={item.to} className={cn(LINK_CLASSES, active(item.to, item.matchPrefix) && ACTIVE_CLASSES)}>
                {item.label}
              </NavLink>
            ),
          )}
        </nav>

        <SearchBox key={location.search} className="ml-auto w-full max-w-xs" />

        <div className="flex shrink-0 items-center gap-1">
          <div className="hidden lg:block">
            <ConnectionControl />
          </div>
          <ThemeSelector />
        </div>
      </div>
    </header>
  );
}
